import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, LayoutGrid, Maximize } from "lucide-react";
import Globe from "./Globe";
import { SLIDES } from "./slides";

const N = SLIDES.length;
const pad = (n) => String(n).padStart(2, "0");

function useStageScale() {
  const [s, setS] = useState(1);
  useEffect(() => {
    const f = () => setS(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    f();
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return s;
}

const toggleFs = () => {
  if (document.fullscreenElement) document.exitFullscreen?.();
  else document.documentElement.requestFullscreen?.();
};

function IndexGrid({ idx, onPick }) {
  return (
    <motion.div className="index" data-testid="slide-index" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="index-head"><span className="kicker">Indice</span><span className="index-hint">Esc / G per chiudere</span></div>
      <div className="index-grid">
        {SLIDES.map((s, i) => (
          <motion.button key={i} className={`thumb ${i === idx ? "on" : ""}`} onClick={() => onPick(i)} data-testid={`index-thumb-${i + 1}`}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
            <span className="thumb-n">{pad(i + 1)}</span>
            <span className="thumb-k">{s.kicker}</span>
            <span className="thumb-t">{s.title}</span>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}

export default function Deck() {
  const [idx, setIdx] = useState(() => {
    const n = parseInt(window.location.hash.replace(/\D/g, ""), 10);
    return n >= 1 && n <= N ? n - 1 : 0;
  });
  const [grid, setGrid] = useState(false);
  const scale = useStageScale();
  const down = useRef(null);
  const go = useCallback((f) => setIdx((i) => Math.max(0, Math.min(N - 1, typeof f === "function" ? f(i) : f))), []);

  useEffect(() => { window.history.replaceState(null, "", `#${idx + 1}`); }, [idx]);
  useEffect(() => {
    const onHash = () => { const n = parseInt(window.location.hash.replace(/\D/g, ""), 10); if (n >= 1 && n <= N) go(n - 1); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [go]);
  useEffect(() => {
    const onKey = (e) => {
      const k = e.key;
      if (k === "f" || k === "F") return toggleFs();
      if (k === "Escape" || k === "g" || k === "G") return setGrid((g) => !g);
      if (grid) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " ", "Enter"].includes(k)) { e.preventDefault(); go((i) => i + 1); }
      else if (["ArrowLeft", "ArrowUp", "PageUp", "Backspace"].includes(k)) { e.preventDefault(); go((i) => i - 1); }
      else if (k === "Home") go(0);
      else if (k === "End") go(N - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [grid, go]);

  const onClick = (e) => {
    if (grid || e.target.closest("button,a,[data-nonav]")) return;
    const d = down.current;
    if (d && Math.hypot(e.clientX - d[0], e.clientY - d[1]) > 6) return;
    go((i) => i + 1);
  };

  const S = SLIDES[idx];
  return (
    <div className="deck" data-testid="deck" onPointerDown={(e) => (down.current = [e.clientX, e.clientY])} onClick={onClick}>
      <Globe cfg={S.globe} slideKey={idx} />
      <div className="vignette" />
      <div className="stage-wrap">
        <div className="stage" style={{ transform: `scale(${scale})` }}>
          <AnimatePresence mode="wait">
            <motion.div key={idx} className="slide-shell" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.04, filter: "blur(12px)" }} transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}>
              <S.C />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      <div className="grain" />
      <div className="chrome" data-testid="deck-chrome">
        <span className="chrome-n" data-testid="slide-number">{pad(idx + 1)} <i>/ {pad(N)}</i></span>
        <span className="chrome-t">{S.title}</span>
        <div className="chrome-btns">
          <button onClick={() => go((i) => i - 1)} data-testid="prev-slide-btn" aria-label="Indietro"><ChevronLeft size={18} /></button>
          <button onClick={() => go((i) => i + 1)} data-testid="next-slide-btn" aria-label="Avanti"><ChevronRight size={18} /></button>
          <button onClick={() => setGrid(true)} data-testid="open-index-btn" aria-label="Indice"><LayoutGrid size={16} /></button>
          <button onClick={toggleFs} data-testid="fullscreen-btn" aria-label="Schermo intero"><Maximize size={16} /></button>
        </div>
      </div>
      <div className="progress" style={{ width: `${((idx + 1) / N) * 100}%` }} data-testid="progress-bar" />
      <AnimatePresence>{grid && <IndexGrid idx={idx} onPick={(i) => { go(i); setGrid(false); }} />}</AnimatePresence>
    </div>
  );
}
